import { create } from "zustand";

import { devtools } from "zustand/middleware";

import type { FlowId, SelfServiceData, StepId } from "../types";

type NavHandler = (stepId: StepId, pushToHistory?: boolean) => void;

type WizardNavHandlers = {
  goToStep: NavHandler;

  goBack: () => void;

  goNext: () => void;
};

interface WizardState {
  flowId: FlowId;

  selfServiceData: SelfServiceData;

  currentStepId: StepId;

  history: StepId[];

  sessionId: string | null;

  hasCompletedFlow: boolean;

  navHandlers: WizardNavHandlers;

  setFlowId: (flowId: FlowId) => void;

  replaceSelfServiceData: (data: SelfServiceData) => void;

  updateSelfServiceData: (field: string, value: unknown) => void;

  setCurrentStepId: (stepId: StepId) => void;

  pushHistory: (step: StepId) => void;

  popHistory: () => StepId | null;

  resetHistory: () => void;

  setSessionId: (sessionId: string | null) => void;

  setHasCompletedFlow: (completed: boolean) => void;

  setNavHandlers: (handlers: WizardNavHandlers) => void;
}

const missingNav: WizardNavHandlers = {
  goToStep: () => {
    throw new Error("Navigation handlers not initialized");
  },

  goBack: () => {
    throw new Error("Navigation handlers not initialized");
  },

  goNext: () => {
    throw new Error("Navigation handlers not initialized");
  },
};

export const useWizardStore = create<WizardState>()(
  devtools((set) => ({
    flowId: "pas",

    selfServiceData: {},

    currentStepId: "start",

    history: [],

    sessionId: null,

    hasCompletedFlow: false,

    navHandlers: missingNav,

    setFlowId: (flowId) =>
      set((state) =>
        state.flowId === flowId ? {} : { flowId, hasCompletedFlow: false },
      ),

    replaceSelfServiceData: (data) =>
      set((state) => ({
        selfServiceData: data ?? {},

        hasCompletedFlow:
          data?.isCompleted === true ? true : state.hasCompletedFlow,
      })),

    updateSelfServiceData: (field, value) =>
      set((state) => {
        const nextData = { ...state.selfServiceData, [field]: value };

        const hasCompletedFlow =
          field === "isCompleted" && value === true
            ? true
            : state.hasCompletedFlow;

        return {
          selfServiceData: nextData,

          hasCompletedFlow,
        };
      }),

    setCurrentStepId: (stepId) => set({ currentStepId: stepId }),

    pushHistory: (step) =>
      set((state) => ({
        history: [...state.history, step],
      })),

    popHistory: () => {
      let popped: StepId | null = null;

      set((state) => {
        if (state.history.length === 0) {
          popped = null;

          return state;
        }

        const nextHistory = state.history.slice(0, -1);

        popped = state.history[state.history.length - 1];

        return { history: nextHistory };
      });

      return popped;
    },

    resetHistory: () => set({ history: [] }),
    setSessionId: (sessionId) => set({ sessionId }),

    setHasCompletedFlow: (completed) => set({ hasCompletedFlow: completed }),

    setNavHandlers: (handlers) => set({ navHandlers: handlers }),
  })),
);
